import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import type { DocsDataState } from '@/models/docs-data/docsData';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import { DOCS_SITE_EVENT } from '@/shared/constant/Analytics';
import { gaReport } from '@/shared/util/analytics';
import AsyncAlgoliaSearchBar from '@/ui-kit/components/algolia-search-bar/AsyncAlgoliaSearchBar';
import {
  isAlgoliaSearchEnabled,
  isLocalSearchEnabled,
} from '@/ui-kit/components/algolia-search-bar/utils';
import { Input } from 'aui';
import { FoxIconSearch, FoxIconX } from 'aui-biz-icons';
import clsx from 'clsx';
import type React from 'react';
import { useCallback, useState } from 'react';
import styles from './index.module.css';

export type SidebarSearchProps = {
  className?: string;
};

const SidebarSearch: React.FC<SidebarSearchProps> = ({ className }) => {
  const [searchKeyword, setSearchKeywordState] = useState('');

  const changeSidebarTreeSearchKeyword = useDocsDataModelStore(
    (x) => x.changeSidebarTreeSearchKeyword,
  );

  const setSearchKeyword = useCallback(
    (value: string) => {
      setSearchKeywordState(value);
      changeSidebarTreeSearchKeyword(value);
    },
    [changeSidebarTreeSearchKeyword, setSearchKeywordState],
  );

  const handleKeywordChangeEvent = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchKeyword(event.target.value);
    },
    [setSearchKeyword],
  );

  const handleCancelSearch = useCallback(() => {
    setSearchKeyword('');
  }, [setSearchKeyword]);
  const title = useDocsDataModelStore((x) => x.docsBaseConfig.title);
  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);
  const searchSettings = useDocsDataModelStore((x) => x.searchSettings);
  const isAlgoliaSearch = isAlgoliaSearchEnabled(searchSettings);
  const isLocalSearch = isLocalSearchEnabled(searchSettings);

  if (isAlgoliaSearch) {
    return (
      <div className={className}>
        <AsyncAlgoliaSearchBar
          appId={searchSettings.appId}
          indexName={searchSettings.indexName}
          apiKey={searchSettings.searchApiKey}
          searchPagePath={false}
        />
      </div>
    );
  }

  if (isLocalSearch) {
    const localSearchSettings = searchSettings as unknown as NonNullable<
      DocsDataState['searchSettings']
    >;
    return (
      <div className={className}>
        <AsyncAlgoliaSearchBar
          appId="appId"
          apiKey="apiKey"
          indexName={localSearchSettings.indexName}
          searchPagePath={false}
          transformToLocalSearch
          localSearchConfig={{
            localSearchIndexDataURL: localSearchSettings.localSearchIndexDataURL,
            localSearchIndexSettingsURL: localSearchSettings.localSearchIndexSettingsURL,
          }}
        />
      </div>
    );
  }

  return (
    <Input
      onChange={handleKeywordChangeEvent}
      placeholder={clientI18n('search')}
      bordered={false}
      value={searchKeyword}
      className={clsx(styles.searchInput, className)}
      prefix={<FoxIconSearch size={14} className={styles.searchInputPrefix} />}
      suffix={
        searchKeyword && (
          <FoxIconX className={styles.searchInputSuffix} size={16} onClick={handleCancelSearch} />
        )
      }
      onClick={() => {
        gaReport(DOCS_SITE_EVENT.INPUT_CLICK_SEARCH, { page_title: title });
      }}
    />
  );
};

export default SidebarSearch;
